import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { stringify } from 'qs';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import ajax from 'helpers/ajax';
import layoutHOC from 'helpers/layout-hoc';
import OpeningsDirectoryLayout from 'apps/portal/layouts/openings-directory';
import LayoutContainer from 'shared/layout-container';
import OpeningsDirectoryIndexFilters from './components/filters';
import OpeningsDirectoryIndexOpenings from './components/openings';
import OpeningsDirectoryIndexPagination from './components/pagination';

class OpeningsDirectoryIndex extends PureComponent {
  static propTypes = {
    accountSlug: PropTypes.string.isRequired,
    apiKeys: PropTypes.shape({
      googleMaps: PropTypes.string,
      googlePlaces: PropTypes.string,
    }),
    ax: PropTypes.bool,
    brandSlug: PropTypes.string,
    countryCode: PropTypes.string,
    translations: PropTypes.shape({
      view: PropTypes.string,
      cityOrZipCodePlaceholder: PropTypes.string,
      clearAllSelections: PropTypes.string,
      jobOpenings: PropTypes.string,
      location: PropTypes.string,
      positions: PropTypes.string,
      radius: PropTypes.string,
      selectPosition: PropTypes.string,
      nonePositions: PropTypes.string,
      showRemoteJobsOnly: PropTypes.string,
      locationIsRequired: PropTypes.string,
      noResultsInRadius: PropTypes.string,
      useCurrentLocation: PropTypes.string,
      hour: PropTypes.string,
    }).isRequired,
    embeddable: PropTypes.bool,
    loadOpenings: PropTypes.func,
    location: PropTypes.shape({
      search: PropTypes.string.isRequired,
    }),
    openings: PropTypes.arrayOf(PropTypes.shape({
      employmentType: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      locationAddress: PropTypes.string,
      locationName: PropTypes.string,
      title: PropTypes.string,
    })),
    pagination: PropTypes.shape({
      currentPage: PropTypes.number,
      nextPage: PropTypes.number,
      perPage: PropTypes.number,
      totalCount: PropTypes.number,
      totalPages: PropTypes.number,
    }),
    params: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    positions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })),
    preview: PropTypes.bool,
    ui: PropTypes.shape({
      isLoading: PropTypes.bool,
    }),
    urlParams: PropTypes.string,
    history: PropTypes.shape().isRequired,
  }

  static defaultProps = {
    apiKeys: {
      googleMaps: undefined,
      googlePlaces: undefined,
    },
    ax: false,
    brandSlug: null,
    countryCode: null,
    loadOpenings: () => {},
    location: {
      search: '',
    },
    openings: [],
    pagination: {
      currentPage: null,
      nextPage: null,
      perPage: null,
      totalCount: null,
      totalPages: null,
    },
    params: {},
    positions: [],
    preview: false,
    ui: {
      isLoading: false,
    },
    urlParams: '',
    embeddable: false,
  }

  constructor(props) {
    super(props);

    const { params } = props;

    this.state = {
      isLoading: true,
      key: 0,
      params,
    };
  }

  /* eslint-disable react/no-did-mount-set-state */
  componentDidMount() {
    const { params } = this.props;
    this._fetchShortCodeApplicantsGeolocation(!!params.keyword_id);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.location.search === this.props.location.search) {
      return;
    }
    const { params } = nextProps;

    const {
      keyword_id: keywordIdParam,
      short_code_applicant_id: shortCodeApplicantIdParam,
    } = params;

    if (keywordIdParam && shortCodeApplicantIdParam) {
      window.localStorage.setItem('keywordId', keywordIdParam);
      window.localStorage.setItem('shortCodeApplicantId', shortCodeApplicantIdParam);
    }

    this.setState({ params });
  }

  _fetchShortCodeApplicantsGeolocation = (isShortCodeApplicant) => {
    if (!isShortCodeApplicant) return this.setState({ isLoading: false });

    return ajax({ method: 'POST', url: '/ip_location' })
      .then(async (response) => {
        await this._updateParams(response.data);
        this.setState({ isLoading: false });
      })
      .catch(() => this.setState({ isLoading: false }));
  }

  _updateParams = (newParams) => {
    const { history: { push } } = this.props;
    const { params } = this.state;

    const cleanedParams = omitBy({
      ...params,
      ...newParams,
    }, isNil);

    this.setState({ params: cleanedParams }, () => {
      const search = stringify(this.state.params, { skipNulls: true });
      push(`?${search}`);
    });
  }

  _resetParams = () => {
    const defaultParams = {
      position_id: null,
      lon: null,
      lat: null,
      distance: null,
      location_external_id: this.state.params.location_external_id || null,
      remote: null,
    };

    const { history: { push } } = this.props;

    this.setState(prevState => ({ params: { ...prevState.params, ...defaultParams }, key: prevState.key + 1 }), () => {
      const search = stringify(this.state.params, { skipNulls: true });
      push(`?${search}`);
    });
  };

  render() {
    if (this.state.isLoading) return null;

    const {
      accountSlug,
      apiKeys,
      ax,
      brandSlug,
      countryCode,
      translations,
      embeddable,
      loadOpenings,
      location,
      openings,
      pagination,
      positions,
      preview,
      ui,
      urlParams,
    } = this.props;

    const { key, params } = this.state;

    // Use the new Places API key
    const selectedApiKeys = {
      ...apiKeys,
      googleMaps: apiKeys.googlePlaces,
    };

    return (
      <LayoutContainer centered size="large">
        <OpeningsDirectoryIndexFilters
          accountSlug={accountSlug}
          apiKeys={selectedApiKeys}
          brandSlug={brandSlug}
          clearAllFilters={this._resetParams}
          countryCode={countryCode}
          loadOpenings={loadOpenings}
          location={location}
          openings={openings}
          params={params}
          positions={positions}
          preview={preview}
          translations={translations}
          ui={ui}
          updateParams={this._updateParams}
          key={key}
        />

        <OpeningsDirectoryIndexOpenings
          accountSlug={accountSlug}
          ax={ax}
          embeddable={embeddable}
          openings={openings}
          pagination={pagination}
          urlParams={urlParams}
          ui={ui}
          translations={translations}
          clearAllFilters={this._resetParams}
        />

        <OpeningsDirectoryIndexPagination
          pagination={pagination}
          updateParams={this._updateParams}
        />
      </LayoutContainer>
    );
  }
}

export default layoutHOC(OpeningsDirectoryIndex, OpeningsDirectoryLayout);
